/**
 * Digitální zoom kamery — veškerá matematika výřezu.
 *
 * Dlaždice si drží stejné místo na obrazovce; mění se jen to, KTERÁ část obrazu je
 * v ní vidět. Stav výřezu je proto rozlišení-nezávislý:
 *
 *   scale   ≥ 1 … 1 = celý obraz, 2 = dvojnásobné přiblížení
 *   cx, cy  … normalizovaný bod OBRAZU (0..1), který sedí ve středu rámu
 *
 * Proč normalizovaně a ne v pixelech: výřez pak přežije změnu režimu dlaždice,
 * spotlight, otočení telefonu, resize i restart streamu s jiným rozlišením — a nic
 * z toho nevyžaduje přepočet. Kdyby byl stav v px, každá z těch událostí by ho musela
 * ručně přeškálovat a jedna zapomenutá cesta znamená ustřelený obraz.
 *
 * Čistý modul bez DOM, protože vitest v tomhle projektu běží v `environment: "node"`
 * bez jsdom — pokrýt testem jde jen to, co je vytažené sem. Stejný vzor jako
 * `cameraLayout.ts` a `ptzHold.ts`.
 *
 * POZOR na souřadnice: `Point` je vždy lokální vůči rámu (tedy už po odečtení
 * `getBoundingClientRect().left/top`), ne klientská souřadnice okna.
 */

export interface Size {
  width: number;
  height: number;
}

/** Bod v pixelech, lokálně vůči rámu (0,0 = levý horní roh rámu). */
export interface Point {
  x: number;
  y: number;
}

/** Normalizovaný bod v CELÉM obrazu kamery. Vlastní typ, ať se neplete s `Point`. */
export interface ImagePoint {
  u: number;
  v: number;
}

/** Kam uvnitř rámu `object-fit` skutečně maluje obraz (v px lokálních vůči rámu). */
export interface PaintedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * `contain` = fill režimy (video má `objectFit: contain`, kolem obrazu můžou být pruhy).
 * `none` = režim `grid`, kde si video drží přirozený poměr a určuje výšku dlaždice,
 * takže namalovaný obraz vyplňuje celý box.
 */
export type ObjectFit = "contain" | "none";

/** Kamera umí jen 4 směry (diagonály nejsou v UI, viz `PtzPad`). */
export type EdgeDirection = "Up" | "Down" | "Left" | "Right";

export interface ZoomView {
  scale: number;
  cx: number;
  cy: number;
}

export interface ZoomLimits {
  min: number;
  max: number;
}

export const IDENTITY_VIEW: ZoomView = { scale: 1, cx: 0.5, cy: 0.5 };

/**
 * Strop 6x. Hlavní stream má 1080p–1440p; ve spotlightu na FullHD monitoru je nativní
 * měřítko zhruba 1x, v malé dlaždici mřížky (~400 px) je nativní detail vyčerpaný
 * kolem 5x. Nad tím už jde jen o interpolační rozmazání. Je to jedna konstanta — kdyby
 * to bylo málo, mění se tady.
 */
export const DEFAULT_ZOOM_LIMITS: ZoomLimits = { min: 1, max: 6 };

/** Tolerance proti plovoucí nule — po pinchi zpět na 1,0000003 nesmí blikat indikátor. */
export const ZOOM_EPS = 0.001;

/** O kolik px musí tažení přetáhnout přes okraj výřezu, než se to počítá jako edge-push. */
export const EDGE_PUSH_THRESHOLD_PX = 24;

const isUsableSize = (s: Size): boolean =>
  Number.isFinite(s.width) && Number.isFinite(s.height) && s.width > 0 && s.height > 0;

/**
 * Kde uvnitř rámu leží skutečně namalovaný obraz.
 *
 * Fallback na celý rám (a ne výjimka) je schválně: než dorazí `loadedmetadata`, je
 * `video.videoWidth` nula a gesta se do té doby musí chovat neškodně, ne padat.
 */
export const computePaintedRect = (container: Size, media: Size, fit: ObjectFit): PaintedRect => {
  const full: PaintedRect = {
    x: 0,
    y: 0,
    width: Number.isFinite(container.width) ? container.width : 0,
    height: Number.isFinite(container.height) ? container.height : 0,
  };
  if (fit === "none" || !isUsableSize(container) || !isUsableSize(media)) return full;

  const containerAspect = container.width / container.height;
  const mediaAspect = media.width / media.height;
  if (mediaAspect > containerAspect) {
    // Širší než rám → pruhy nahoře a dole.
    const width = container.width;
    const height = width / mediaAspect;
    return { x: 0, y: (container.height - height) / 2, width, height };
  }
  // Užší (nebo stejný poměr) → pruhy po stranách.
  const height = container.height;
  const width = height * mediaAspect;
  return { x: (container.width - width) / 2, y: 0, width, height };
};

export const clampScale = (scale: number, limits: ZoomLimits = DEFAULT_ZOOM_LIMITS): number => {
  // Jen NaN je bez smyslu; +-Infinity je legitimní „hodně" a ořízne se samo na mez.
  if (Number.isNaN(scale)) return limits.min;
  return Math.min(limits.max, Math.max(limits.min, scale));
};

/**
 * Polovina viditelného rozsahu v normalizovaných souřadnicích obrazu.
 *
 * Bere v úvahu letterbox: když je namalovaný obraz užší než rám, vidí se v té ose víc
 * než 1/scale obrazu (na okrajích jsou pruhy) — a clamp na to musí reagovat, jinak by
 * šlo zazoomovaně odjet tak, že se pruh objeví uprostřed pohledu.
 */
export const visibleHalfExtent = (scale: number, containerSize: number, paintedSize: number): number => {
  if (!Number.isFinite(paintedSize) || paintedSize <= 0) return 0.5;
  if (!Number.isFinite(scale) || scale <= 0) return 0.5;
  return containerSize / 2 / (scale * paintedSize);
};

/**
 * Ořízne měřítko do limitů a střed tak, aby výřez nevylezl z obrazu.
 *
 * Při `scale = 1` (a bez letterboxu) vyjde `half = 0,5`, interval se scvrkne na jediný
 * bod 0,5 → nezazoomovaný obraz nejde posouvat. To pravidlo tedy nikde není napsané
 * zvlášť, plyne samo z matematiky.
 */
export const clampView = (
  view: ZoomView,
  container: Size,
  painted: PaintedRect,
  limits: ZoomLimits = DEFAULT_ZOOM_LIMITS
): ZoomView => {
  const scale = clampScale(view.scale, limits);
  const hx = visibleHalfExtent(scale, container.width, painted.width);
  const hy = visibleHalfExtent(scale, container.height, painted.height);
  const cxRaw = Number.isFinite(view.cx) ? view.cx : 0.5;
  const cyRaw = Number.isFinite(view.cy) ? view.cy : 0.5;
  const cx = hx >= 0.5 ? 0.5 : Math.min(1 - hx, Math.max(hx, cxRaw));
  const cy = hy >= 0.5 ? 0.5 : Math.min(1 - hy, Math.max(hy, cyRaw));
  return { scale, cx, cy };
};

/** Bod v rámu → normalizovaný bod obrazu. Inverzní k `imagePointToScreen`. */
export const screenToImagePoint = (view: ZoomView, container: Size, painted: PaintedRect, p: Point): ImagePoint => {
  if (painted.width <= 0 || painted.height <= 0) return { u: view.cx, v: view.cy };
  return {
    u: view.cx + (p.x - container.width / 2) / (view.scale * painted.width),
    v: view.cy + (p.y - container.height / 2) / (view.scale * painted.height),
  };
};

/** Normalizovaný bod obrazu → bod v rámu. */
export const imagePointToScreen = (view: ZoomView, container: Size, painted: PaintedRect, ip: ImagePoint): Point => ({
  x: container.width / 2 + view.scale * painted.width * (ip.u - view.cx),
  y: container.height / 2 + view.scale * painted.height * (ip.v - view.cy),
});

/**
 * Zoom tak, aby bod pod kurzorem zůstal pod kurzorem.
 *
 * Kotva se spočítá ve STARÉM pohledu, pak se hledá `cx,cy`, při kterých ta samá kotva
 * padne na stejné místo v NOVÉM měřítku. U okraje obrazu clamp kotvu nutně o kousek
 * posune — to je očekávané, alternativa by byla ukázat pruh mimo obraz.
 */
export const zoomAt = (
  view: ZoomView,
  container: Size,
  painted: PaintedRect,
  screenPoint: Point,
  factor: number,
  limits: ZoomLimits = DEFAULT_ZOOM_LIMITS
): ZoomView => {
  if (!Number.isFinite(factor) || factor <= 0) return view;
  if (painted.width <= 0 || painted.height <= 0) return view;
  const anchor = screenToImagePoint(view, container, painted, screenPoint);
  const newScale = clampScale(view.scale * factor, limits);
  const cx = anchor.u - (screenPoint.x - container.width / 2) / (newScale * painted.width);
  const cy = anchor.v - (screenPoint.y - container.height / 2) / (newScale * painted.height);
  return clampView({ scale: newScale, cx, cy }, container, painted, limits);
};

/**
 * Posun výřezu o daný počet px na obrazovce.
 *
 * Vrací i `overflowPx` — kolik z požadovaného posunu clamp odmítl. To je jediný vstup
 * pro edge-push (fyzické otočení kamery), takže se nesmí zahodit.
 *
 * Znaménka (snadno se to splete, tak polopatě): tažení doprava (`dxPx > 0`) SNIŽUJE
 * `cx`, tedy obsah jde s prstem a odkrývá se to, co bylo vlevo. Když je `cx` už na
 * spodní mezi (výřez úplně vlevo) a táhne se dál doprava, vyjde `overflowPx.x`
 * ZÁPORNÝ — a znamená to „chci ještě víc doleva", tedy otoč kameru doleva.
 */
export const panByClamped = (
  startView: ZoomView,
  container: Size,
  painted: PaintedRect,
  dxPx: number,
  dyPx: number,
  limits: ZoomLimits = DEFAULT_ZOOM_LIMITS
): { view: ZoomView; overflowPx: Point } => {
  if (painted.width <= 0 || painted.height <= 0) return { view: startView, overflowPx: { x: 0, y: 0 } };
  const desiredCx = startView.cx - dxPx / (startView.scale * painted.width);
  const desiredCy = startView.cy - dyPx / (startView.scale * painted.height);
  const clamped = clampView({ scale: startView.scale, cx: desiredCx, cy: desiredCy }, container, painted, limits);
  return {
    view: clamped,
    overflowPx: {
      x: (desiredCx - clamped.cx) * startView.scale * painted.width,
      y: (desiredCy - clamped.cy) * startView.scale * painted.height,
    },
  };
};

export const isZoomed = (view: ZoomView, eps: number = ZOOM_EPS): boolean => view.scale > 1 + eps;

/**
 * Došlo tažením na okraj výřezu a táhne se dál? Pak je na řadě fyzické PTZ.
 *
 * Gate na `scale > 1` je uvnitř schválně: při nezazoomovaném obrazu je na okraji úplně
 * každé tažení, takže bez toho by edge-push spouštěl každý swipe.
 */
export const edgePushDirection = (
  view: ZoomView,
  overflowPx: Point,
  thresholdPx: number = EDGE_PUSH_THRESHOLD_PX
): EdgeDirection | null => {
  if (!isZoomed(view)) return null;
  const ax = Math.abs(overflowPx.x);
  const ay = Math.abs(overflowPx.y);
  if (ax < thresholdPx && ay < thresholdPx) return null;
  // Vyhrává dominantní osa; při rovnosti vodorovná (kamera diagonály neumí).
  if (ax >= ay) return overflowPx.x > 0 ? "Right" : "Left";
  return overflowPx.y > 0 ? "Down" : "Up";
};

/**
 * CSS pro video element.
 *
 * `transform-origin: 0 0` plus jediná `matrix(...)`: box videa je ve VŠECH režimech
 * totožný s boxem rámu (ve fill režimech `h-full w-full`, v `grid` určuje výšku rámu
 * samo video), takže stačí jedna formule a liší se jen předaný `painted` rect.
 * `matrix` místo `translate(...) scale(...)` proto, že nezávisí na tom, jestli čtenář
 * ví, že se CSS transformace skládají zprava doleva — a v testu se dá porovnat řetězec.
 *
 * Kontrola správnosti: pro `IDENTITY_VIEW` je `painted.x + 0,5 * painted.width` přesně
 * střed rámu, takže `tx = ty = 0` a vyjde `matrix(1, 0, 0, 1, 0, 0)` — tedy přesně to,
 * co se renderuje dnes bez zoomu.
 */
export const toCssTransform = (
  view: ZoomView,
  container: Size,
  painted: PaintedRect
): { transform: string; transformOrigin: string } => {
  const { scale, cx, cy } = view;
  const tx = container.width / 2 - scale * (painted.x + cx * painted.width);
  const ty = container.height / 2 - scale * (painted.y + cy * painted.height);
  return { transform: `matrix(${scale}, 0, 0, ${scale}, ${tx}, ${ty})`, transformOrigin: "0 0" };
};
